import {Injectable, EventEmitter} from '@angular/core';
import {Router, NavigationEnd, ActivatedRoute, NavigationStart} from '@angular/router';
import {Subject, asyncScheduler} from 'rxjs';
import {throttleTime, mergeMap} from 'rxjs/operators';
import { StorageHelperService } from '../storageHelper/storage-helper.service';

interface UrlHistory {
    url: string;
    queryParams?: any;
}

@Injectable({
    providedIn: 'root'
})


export class NavigationHelperService {
    static singletonInstance: NavigationHelperService;

    /**
     * Stores routing history, if query param changed in same url only latest copy will be stored rest ignored
     */
    private _history: Array<UrlHistory> = [];

    navigateToPreviousUrl$ = new Subject;

    private localStoragekey = 'previousUrl';

    /**
     * Number of items to store per session
     */
    private STORAGE_LIMIT = 20;


    constructor(
        public router: Router,
        public activatedRoute: ActivatedRoute,
        private storageHelperService: StorageHelperService
    ) {
        this.handlePrevNavigation();
        if (!NavigationHelperService.singletonInstance) {
            NavigationHelperService.singletonInstance = this;
        }
        return NavigationHelperService.singletonInstance;
    }

    /**
     * Stores routing history
     * NavigationHelperService
     */
    private storeUrlHistory(): void {
        this.router.events.subscribe(e => {
            if (e instanceof NavigationStart) {
            } else if (e instanceof NavigationEnd) {
                const urlAfterRedirects = e;
                const queryParams = this.activatedRoute.root.children[this.activatedRoute.root.children.length - 1].snapshot.queryParams;
                const url = urlAfterRedirects.url.split('?')[0];
                let history: UrlHistory;
                if (!queryParams) {
                    history = {url};
                } else {
                    history = {url, queryParams};
                }
                const previousUrl = this._history.pop();
                if (previousUrl === undefined || (previousUrl && previousUrl.url === history.url)) {
                    this._history.push(history);
                } else {
                    this._history.push(previousUrl, history);
                }
                if (this._history.length > this.STORAGE_LIMIT) {
                    this._history = this._history.slice(this._history.length - this.STORAGE_LIMIT, this._history.length);
                }
                this.storageHelperService.storeEncryptedData(this.localStoragekey, JSON.stringify(this._history || []));
            }
        });
    }


    /**
     * returns routing history
     */
    get history(): Array<UrlHistory> {
        return this._history;
    }

    /**
     * initialize storeUrlHistory function to store routing history.
     * Add callback function for window.onunload to store previous url.
     */
    initialize() {
        const localStorageData = this.storageHelperService.getDecryptData(this.localStoragekey) || '[]';
        const sessionData = JSON.parse(localStorageData);
        if (sessionData && sessionData !== '[]') {
            this._history = sessionData;
        }
        this.storeUrlHistory();
    }

    /**
     * returns PreviousUrl
     * 1. First fetches from _history property.
     * 2. From session if _history is not present, for reload case.
     * 3. if both are not present then default explore is returned.
     */
    public getPreviousUrl(): UrlHistory {
        const previousUrl = this.history[this._history.length - 2];
        const localStorageData = this.storageHelperService.getDecryptData(this.localStoragekey) || '{}';
        const sessionUrl = JSON.parse(localStorageData);
        if (previousUrl) {
            return previousUrl;
        } else if (sessionUrl) {
            return sessionUrl;
        } else {
            return {url: ''};
        }
    }

    /**
     * Navigates to previous Url
     * moved logic to subject subscription to prevent,
     * multiple navigation if user click close multiple time before navigation trigers
     */
    public navigateToPreviousUrl(defaultUrl: string = '/') {
        this.navigateToPreviousUrl$.next(defaultUrl);
    }


    handlePrevNavigation() {
        this.navigateToPreviousUrl$.pipe(
            throttleTime(250, asyncScheduler, {leading: true, trailing: false}))
            .subscribe(defaultUrl => {
                const previousUrl = this.getPreviousUrl();
                this._history.pop(); // popping current url
                this._history.pop(); // popping previous url as am navigating to same url it will be added again
                if (previousUrl.queryParams) {
                    this.router.navigate([previousUrl.url], {queryParams: previousUrl.queryParams});
                } else {
                    this.router.navigate([previousUrl.url]);
                }
            });
    }

}
